import { useEffect, useState } from 'react';
import { connectAPI } from '@services/photographerApi';
import type { ConnectStatus } from '@/types/photographer';

const REASON_LABEL: Record<string, string> = {
  subscription_inactive: 'You need an active subscription. Choose a plan on the Billing page.',
  connect_incomplete: 'You need to set up payouts (Stripe Connect) on the Billing page.',
};

interface Props {
  eventId: string;
  status: string;
  onChange: (status: string) => void;
}

export default function PublishControl({ eventId, status, onChange }: Props) {
  const [connect, setConnect] = useState<ConnectStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    connectAPI.getStatus().then(setConnect).catch(() => setConnect(null));
  }, []);

  const isPublished = status === 'published';

  const publish = async () => {
    setBusy(true);
    setError(null);
    try {
      await connectAPI.publishEvent(eventId);
      onChange('published');
    } catch (e: any) {
      const reasons: string[] = e?.response?.data?.detail?.reasons ?? [];
      setError(reasons.map(r => REASON_LABEL[r] ?? r).join(' ') || 'Could not publish.');
    } finally {
      setBusy(false);
    }
  };

  const unpublish = async () => {
    setBusy(true);
    setError(null);
    try {
      await connectAPI.unpublishEvent(eventId);
      onChange('draft');
    } catch {
      setError('Could not unpublish.');
    } finally {
      setBusy(false);
    }
  };

  const eligible = connect?.eligible ?? false;

  return (
    <div className="space-y-2">
      {isPublished ? (
        <button
          type="button"
          onClick={unpublish}
          disabled={busy}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-50"
        >
          Unpublish
        </button>
      ) : (
        <button
          type="button"
          onClick={publish}
          disabled={busy || !eligible}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          Publish
        </button>
      )}

      {!isPublished && !eligible && connect && (
        <ul className="text-sm text-amber-700 list-disc pl-5">
          {connect.reasons.map(r => (
            <li key={r}>{REASON_LABEL[r] ?? r}</li>
          ))}
        </ul>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}
